var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Form_1;
import React, { Component, createRef, Fragment } from 'react';
import _set from 'lodash.set';
import { CSSTransition } from 'react-transition-group';
import { Density } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableSubmitEvent, ChangeEvent, EditEvent, SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, createAbort, deepMerge, enumValues, isEmptyOrBlankString, isEnumValid, isEqualShallow, isFunction, noop, propagateData, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { sortChildrenByPropName } from '../../utils/sort-children-by-prop-name.js';
import { Loader } from '../loader/loader.js';
import { Modal } from '../modal/modal.js';
import { FormActions } from './components/form-actions.js';
import { FormEditAction } from './components/form-edit-action.js';
import { FormHeader } from './components/form-header.js';
import { FormContext, FormProvider } from './context/form-context.js';
import FormAPI from './form-api.js';
import { FormEditingMode } from './form-constants.js';
import './form.css';
const shallowCompareProps = [
    'children',
    'className',
    'data',
    'density',
    'disabled',
    'editing',
    'loading',
    'locked',
    'title',
    'uuid',
];
const shallowCompareState = [
    'innerEditing',
    'prettyRecord',
    'record',
    'recordChanges',
    'submitting',
];
const FormContainer = {
    acceptedTypes: [
        ComponentType.FORM,
    ],
    name: '',
};
let Form = Form_1 = class Form extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            __validation: {
                reset: () => {
                    return this.reset();
                },
                validate: (notify = false) => {
                    return this.validate(true, notify);
                },
            },
            innerEditing: this.props.editing ?? Form_1.defaultProps.editing,
            prettyRecord: {},
            record: this.props.data ?? Form_1.defaultProps.data,
            recordChanges: {},
            submitting: false,
        };
        this.formProviderRef = createRef();
        this.unlistenNestedReset = noop;
        this.unlistenNestedValidate = noop;
        this.reset = () => {
            this.formProviderRef.current?.reset();
            this.setState({ recordChanges: {} });
        };
        this.validate = (notifyChildren = false, notifyListeners = false) => {
            const { current } = this.formProviderRef;
            if (!current) {
                return [false, []];
            }
            const [valid, validations] = current.validate(this.getRecord(), notifyChildren);
            if (notifyListeners && isFunction(this.props.onValidate)) {
                const event = new ValidateEvent({ valid, validations });
                this.props.onValidate(event);
            }
            return [valid, validations];
        };
        this.onCancel = () => {
            const { onBeforeCancel, onCancel } = this.props;
            const { prettyRecord, record } = this.state;
            const abort = createAbort();
            if (isFunction(onBeforeCancel)) {
                onBeforeCancel(new AbortableSubmitEvent({ abort, prettyRecord, record }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return;
                }
                if (isFunction(onCancel)) {
                    onCancel(new SubmitEvent({ prettyRecord, record }));
                }
                this.setState({ recordChanges: {} });
                this.setEditting(false);
            });
        };
        this.onChange = (event) => {
            const { entry, prettyValue, value } = event;
            const { innerEditing, recordChanges: oldRecordChanges } = this.state;
            const callback = () => {
                const { onChange } = this.props;
                const { prettyRecord, record } = this.state;
                if (isFunction(onChange)) {
                    onChange(new ChangeEvent({
                        oldValue: deepMerge(record, oldRecordChanges),
                        prettyValue: prettyRecord,
                        value: this.getRecord(),
                    }));
                }
            };
            if (!entry || !innerEditing) {
                return;
            }
            this.setState(prevState => ({
                prettyRecord: _set({ ...prevState.prettyRecord }, entry, prettyValue),
                recordChanges: _set({ ...prevState.recordChanges }, entry, value),
            }), callback);
        };
        this.onSubmit = () => {
            const [valid] = this.validate(true, false);
            if (!valid) {
                return;
            }
            const { onBeforeSubmit, onSubmit } = this.props;
            const { prettyRecord } = this.state;
            const abort = createAbort();
            const record = this.getRecord();
            this.setState({ submitting: true });
            if (isFunction(onBeforeSubmit)) {
                onBeforeSubmit(new AbortableSubmitEvent({ abort, prettyRecord, record }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return this.setState({ submitting: false });
                }
                if (isFunction(onSubmit)) {
                    onSubmit(new SubmitEvent({ prettyRecord, record }));
                }
                this.setState({
                    prettyRecord,
                    record,
                    recordChanges: {},
                    submitting: false,
                });
                this.setEditting(false);
            });
        };
        this.onValidate = () => {
            this.validate(false, true);
        };
        this.toggleEdit = () => {
            const { locked } = this.props;
            if (locked) {
                return;
            }
            const callback = () => {
                const { onEdit } = this.props;
                const { innerEditing } = this.state;
                if (innerEditing && isFunction(onEdit)) {
                    onEdit(new EditEvent());
                }
            };
            this.setState(prevState => ({ innerEditing: !prevState.innerEditing }), callback);
        };
        this.isValid = () => {
            const [valid] = this.validate(false, false);
            return valid;
        };
        this.getValue = () => {
            return this.getRecord();
        };
        this.setValue = (record) => {
            this.setState({ record, recordChanges: {} });
        };
    }
    componentDidMount() {
        if (!this.context) {
            return;
        }
        this.unlistenNestedReset = this.context.addResetListener(() => {
            if (!this.formProviderRef.current) {
                return;
            }
            this.formProviderRef.current.reset();
        });
        this.unlistenNestedValidate = this.context.addNestedValidate(() => {
            if (!this.formProviderRef.current) {
                return [true, []];
            }
            return this.formProviderRef.current.validate(this.getRecord(), true);
        });
    }
    shouldComponentUpdate(nextProps, nextState) {
        if (shallowCompare(this.props, nextProps, shallowCompareProps)) {
            return true;
        }
        if (shallowCompare(this.state, nextState, shallowCompareState)) {
            return true;
        }
        return false;
    }
    componentDidUpdate(prevProps) {
        const { data = Form_1.defaultProps.data, editing } = this.props;
        if (prevProps.editing !== editing) {
            this.setState({ innerEditing: Boolean(editing) });
        }
        if (!isEqualShallow(data, prevProps.data)) {
            this.setState({ record: data, recordChanges: {} }, () => this.validate(true, false));
        }
    }
    shouldRenderActions() {
        return this.state.innerEditing && !this.props.disabled;
    }
    setEditting(value) {
        const { locked } = this.props;
        this.setState(prevState => ({
            innerEditing: locked ? prevState.innerEditing : value,
        }));
    }
    getClassName() {
        const { className } = this.props;
        return classNames({
            'c-form': true,
            [`${className}`]: Boolean(className),
        });
    }
    getFormClassName() {
        const { density } = this.props;
        return classNames({
            'o-form': true,
            [`o-form--${density}`]: isEnumValid(Density, density),
        });
    }
    getRecord() {
        const { record, recordChanges } = this.state;
        return deepMerge(record, recordChanges);
    }
    renderFields() {
        const { children, density } = this.props;
        const { innerEditing } = this.state;
        const record = this.getRecord();
        return propagateData(sortChildrenByPropName(children, 'order'), record, {
            className: 'o-form__field',
            density,
            editing: innerEditing,
            onChange: this.onChange,
            onValidate: this.onValidate,
            readOnly: !innerEditing,
        });
    }
    renderHeader() {
        const { disabled, locked, showHeader, title } = this.props;
        const { innerEditing } = this.state;
        if (!showHeader || disabled || innerEditing && isEmptyOrBlankString(title)) {
            return null;
        }
        const showEditAction = !innerEditing && !locked && !disabled;
        return (React.createElement(FormHeader, { title: title }, showEditAction ? (React.createElement(FormEditAction, { onClick: this.toggleEdit, tabIndex: this.props.editActionTabIndex })) : null));
    }
    renderSubmittingOverlay() {
        if (!this.state.submitting) {
            return null;
        }
        return (React.createElement("div", { className: "c-form__submitting-overlay" }));
    }
    renderActions() {
        if (!this.shouldRenderActions()) {
            return null;
        }
        const { cancelExpression = Form_1.defaultProps.cancelExpression, saveExpression = Form_1.defaultProps.saveExpression } = this.props;
        const { submitting } = this.state;
        return (React.createElement(FormActions, { cancelExpression: cancelExpression, onCancel: this.onCancel, onSubmit: this.onSubmit, saveExpression: saveExpression, submitting: submitting }));
    }
    renderLoader() {
        const { loading } = this.props;
        return (React.createElement(CSSTransition, { classNames: "c-form__loader--fade", in: loading, timeout: 150, unmountOnExit: true },
            React.createElement(Loader, { centered: true, className: "c-form__loader" })));
    }
    render() {
        const { as: CustomFormTag = Form_1.defaultProps.as, customSlot, editingMode = Form_1.defaultProps.editingMode, title, uuid, } = this.props;
        const { innerEditing } = this.state;
        const role = CustomFormTag !== 'form' ? 'form' : undefined;
        const content = (React.createElement(Fragment, null,
            React.createElement(Container, { acceptedTypes: FormContainer.acceptedTypes, className: "c-form__container", "data-area-name": customSlot, uuid: uuid },
                React.createElement(CustomFormTag, { "aria-label": MessageService.translate(title ?? ''), className: this.getFormClassName(), onSubmit: event => event.preventDefault(), role: role },
                    this.renderSubmittingOverlay(),
                    this.renderFields())),
            this.renderActions(),
            this.renderLoader()));
        let wrappedContent = (React.createElement(Fragment, null, content));
        if (innerEditing && editingMode === FormEditingMode.EXTERNAL) {
            wrappedContent = (React.createElement(Modal, { closeable: false, onClose: this.onCancel, title: title }, content));
        }
        return (React.createElement(FormProvider, { ref: this.formProviderRef },
            React.createElement("div", { className: this.getClassName() },
                this.renderHeader(),
                wrappedContent)));
    }
};
Form.contextType = FormContext;
Form.propTypes = {
    as: oneOf(['form', 'div']).notEnumerable(),
    cancelExpression: expression,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    customSlot: string.notEnumerable(),
    data: object.notEnumerable(),
    density: oneOf(Object.values(Density)),
    disabled: bool,
    editActionTabIndex: number,
    editing: bool,
    editingMode: oneOf(enumValues(FormEditingMode)),
    loading: bool,
    locked: bool,
    onBeforeCancel: eventListener,
    onBeforeSubmit: eventListener,
    onCancel: eventListener,
    onChange: eventListener,
    onEdit: eventListener,
    onSubmit: eventListener,
    onValidate: eventListener,
    saveExpression: expression,
    showHeader: bool.notEnumerable(),
    slot: string.notEnumerable(),
    title: expression,
    uuid: string,
};
Form.defaultProps = {
    as: 'form',
    cancelExpression: 'tws.form.cancel',
    data: {},
    density: Density.REGULAR,
    disabled: false,
    editing: false,
    editingMode: FormEditingMode.DEFAULT,
    loading: false,
    locked: false,
    onCancel: noop,
    saveExpression: 'tws.form.save',
    showHeader: true,
    title: '',
    uuid: '',
};
Form = Form_1 = __decorate([
    AwesomeComponent({ api: FormAPI, containers: [FormContainer], type: ComponentType.FORM })
], Form);
export { Form, };
export default Form;
//# sourceMappingURL=form.js.map